import React, { useState } from "react";
import {
  FileText,
  Link as LinkIcon,
  UploadCloud,
  Sparkles,
  AlertCircle,
} from "lucide-react";

import FileUploadTab from "./demo/FileUploadTab";
import LinkTab from "./demo/LinkTab";
import PasteTextTab from "./demo/PasteTextTab";
import { api } from "./demo/api_service/api";
import ResultsModal from "./demo/ResultsModal";
import { AnalysisResult, InputMode } from "./demo/types";

const Demo: React.FC = () => {
  const [mode, setMode] = useState<InputMode>("text");
  const [isAnalyzing, setIsAnalyzing] = useState<boolean>(false);
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [inputText, setInputText] = useState<string>("");
  const [inputLink, setInputLink] = useState<string>("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleAnalyze = async (): Promise<void> => {
    if (isAnalyzing) return;

    setError(null);
    setResult(null);
    setIsAnalyzing(true);

    try {
      await api.login();

      let data: AnalysisResult;

      switch (mode) {
        case "text": {
          const wordCount = inputText.trim().split(/\s+/).length;
          if (wordCount < 50) {
            setError("Please enter at least 50 words to analyze.");
            setIsAnalyzing(false);
            return;
          }
          data = await api.analyzeText(inputText);
          break;
        }
        case "link": {
          const trimmedUrl = inputLink.trim();
          if (!trimmedUrl) {
            setError("Please enter a valid URL.");
            setIsAnalyzing(false);
            return;
          }
          try {
            new URL(trimmedUrl);
            if (
              !trimmedUrl.startsWith("http://") &&
              !trimmedUrl.startsWith("https://")
            ) {
              throw new Error("URL must start with http:// or https://");
            }
          } catch (urlErr) {
            setError(
              "Please enter a valid URL starting with http:// or https://."
            );
            setIsAnalyzing(false);
            return;
          }
          data = await api.analyzeLink(trimmedUrl);
          break;
        }
        case "file": {
          if (!selectedFile) {
            setError("Please select a file to analyze.");
            setIsAnalyzing(false);
            return;
          }
          data = await api.analyzeFile(selectedFile);
          break;
        }
        default:
          const _exhaustiveCheck: never = mode;
          throw new Error(`Invalid mode: ${_exhaustiveCheck}`);
      }

      requestAnimationFrame(() => {
        setIsAnalyzing(false);
        setResult(data);
      });
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Analysis failed. Please try again.";

      setIsAnalyzing(false);
      setError(errorMessage);
    }
  };

  const renderTabButton = (
    tabMode: InputMode,
    label: string,
    Icon: React.ComponentType<{ size?: number }>
  ) => (
    <button
      onClick={() => {
        setMode(tabMode);
        setResult(null);
      }}
      className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-all duration-200 border-b-2 ${
        mode === tabMode
          ? "border-indigo-600 text-indigo-600 bg-indigo-50/50"
          : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"
      }`}
      type="button"
    >
      <Icon size={18} />
      {label}
    </button>
  );

  const getButtonDisabled = (): boolean => {
    const wordCount = inputText.trim().split(/\s+/).length;
    const trimmedUrl = inputLink.trim();
    let isValidUrl = false;
    if (trimmedUrl) {
      try {
        new URL(trimmedUrl);
        isValidUrl =
          trimmedUrl.startsWith("http://") || trimmedUrl.startsWith("https://");
      } catch {
        isValidUrl = false;
      }
    }
    return (
      isAnalyzing ||
      (mode === "text" && wordCount < 50) ||
      (mode === "link" && (!trimmedUrl || !isValidUrl)) ||
      (mode === "file" && !selectedFile)
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50 text-slate-800 font-sans selection:bg-indigo-100">
      <div className="max-w-3xl mx-auto px-4">
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3 text-red-800">
            <AlertCircle size={20} />
            <span className="text-sm">{error}</span>
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden">
          <div className="flex border-b border-slate-100">
            {renderTabButton("text", "Paste Text", FileText)}
            {renderTabButton("link", "Link / Drive", LinkIcon)}
            {renderTabButton("file", "Upload File", UploadCloud)}
          </div>

          <div className="p-6 md:p-8">
            {mode === "text" && (
              <PasteTextTab value={inputText} onChange={setInputText} />
            )}
            {mode === "link" && (
              <LinkTab value={inputLink} onChange={setInputLink} />
            )}
            {mode === "file" && (
              <FileUploadTab
                file={selectedFile}
                onFileChange={setSelectedFile}
              />
            )}

            <div className="mt-8 pt-6 border-t border-slate-100 flex justify-end">
              <button
                onClick={handleAnalyze}
                disabled={getButtonDisabled()}
                className={`flex items-center gap-2 px-8 py-3 rounded-xl font-semibold text-white shadow-lg shadow-indigo-200 transition-all ${
                  isAnalyzing
                    ? "bg-indigo-400 cursor-wait"
                    : "bg-indigo-600 hover:bg-indigo-700 hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-50 disabled:cursor-not-allowed"
                }`}
                type="button"
              >
                {isAnalyzing ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Sparkles size={18} />
                    Analyze Content
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {result && (
          <ResultsModal result={result} onClose={() => setResult(null)} />
        )}
      </div>
    </div>
  );
};

export default Demo;
